/**
 * The building's chat, on a computer or a phone's browser.
 *
 * The same room the phone app has, read and written the same way. Residents
 * and the committee read one room. The committee also keeps order in it:
 * pinning a message or a fundraiser above it, deleting a message, turning
 * somebody's chat off for a while or until further notice, putting a name back
 * to the door, setting how long residents wait between messages, and closing
 * the room to everybody but the committee. The rules enforce every one of
 * those; the page says so plainly before the server has to refuse anything.
 *
 * Nobody can be removed. A resident is part of the building whether or not
 * they may post, so the most the committee can do is turn their chat off, and
 * they keep reading.
 *
 * A committee message is drawn in gold with its tag, because the role on a
 * message is checked against the sender's account and so is the one thing in
 * the room nobody can pretend to. A resident who picks a name still shows their
 * door beside it, so nobody can talk as somebody else's unit.
 *
 * The screen is redrawn from the store on every change like every other one.
 * What has to outlive a redraw lives here: which panel is open, whether the
 * log is following the newest message, and the countdown, which changes the
 * page in place every second rather than redrawing it under somebody's typing.
 */

import {
  CHAT_COOLDOWNS,
  CHAT_MESSAGE_MAX,
  CHAT_NAME_MAX,
  CHAT_OFF_SPANS,
  CHAT_PINNED_CAMPAIGNS_MAX,
  type ChatOffSpan,
  chatIsOff,
  chatVoice,
  offers,
  pinCampaign,
  secondsToWait,
} from "@liyu/core";
import * as data from "./data";
import { esc, hold, toast } from "./dom";
import { bar, money, on } from "./pieces";
import type { Session } from "./session";
import { refresh, store } from "./store";
import { language, t } from "./strings";
import type { Key } from "./strings";
import type { View } from "./views";

type Panel =
  | { kind: "rename" }
  | { kind: "manage" }
  | { kind: "message"; id: string }
  | { kind: "person"; uid: string }
  | null;

let panel: Panel = null;
/** Set when a panel opens, so the redraw that shows it can put focus inside it. */
let focusPanel = false;
/** Whether the log is at its newest message and should stay there as more arrive. */
let following = true;
let scrolled = 0;
let sending = false;
let tick: number | undefined;
let keysWired = false;
let pageWired = false;

const SEEN = "liyu.chatSeenAt.";
const seen = new Map<string, number>();

/**
 * When this browser last had the chat open, for this account, for the unread
 * count in the band. Kept in the browser rather than on the account, the way
 * the phone keeps the board's: "have I read this" is about the person at this
 * screen. A first visit counts as now, so somebody opening Liyu for the first
 * time is not greeted with "10+" for a conversation they were never part of.
 */
export function chatSeenAt(uid: string): number {
  const known = seen.get(uid);
  if (known !== undefined) return known;
  let at = Date.now();
  try {
    const stored = Number(localStorage.getItem(SEEN + uid) ?? Number.NaN);
    if (Number.isFinite(stored)) {
      at = stored;
    } else {
      localStorage.setItem(SEEN + uid, String(at));
    }
  } catch {
    // A browser that refuses site data counts from this visit; the mark is a
    // convenience and the chat still works.
  }
  seen.set(uid, at);
  return at;
}

/** Everything sent up to `at` has been seen. Never moves backwards. */
export function markChatSeen(uid: string, at: number): void {
  if (at <= chatSeenAt(uid)) return;
  seen.set(uid, at);
  try {
    localStorage.setItem(SEEN + uid, String(at));
  } catch {
    // As above: remembered for this visit only.
  }
}

/** A fresh visit to the chat: at the newest message, with nothing open. */
export function openedChat(): void {
  panel = null;
  following = true;
}

type Named = Pick<data.ChatMessage, "role" | "unitId" | "displayName">;

/** What a person is called in the room, and their door when they chose a name. */
function nameOf(person: Named): { name: string; door: string | null } {
  const fallback = person.role === "committee" ? t("chat.committee") : (person.unitId ?? "");
  return {
    name: person.displayName || fallback,
    door: person.role === "resident" && person.displayName ? person.unitId : null,
  };
}

function locale(): string {
  return language() === "am" ? "am-ET" : "en-GB";
}

/** The time a message was sent, with its date when it was not today. */
function sentWhen(date: Date | null): string {
  if (!date) return "";
  const time = date.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
  return date.toDateString() === new Date().toDateString() ? time : `${on(date)} ${time}`;
}

function moment(date: Date): string {
  return date.toLocaleString(locale(), { dateStyle: "medium", timeStyle: "short" });
}

function cooldownLabel(seconds: number): string {
  if (seconds === 0) return t("chat.cooldownNone");
  return seconds < 60
    ? t("chat.seconds", { count: seconds })
    : t("chat.minutes", { count: seconds / 60 });
}

function offLabel(span: ChatOffSpan): string {
  switch (span) {
    case 3600:
      return t("chat.forAnHour");
    case 86_400:
      return t("chat.forADay");
    case 604_800:
      return t("chat.forAWeek");
    default:
      return t("chat.untilTurnedOn");
  }
}

/** How somebody stands in the room, for the committee's list and their panel. */
function standing(member: data.ChatMember, now: Date): string | null {
  if (!chatIsOff(member, now)) return null;
  return member.mutedUntil
    ? t("chat.isOffUntil", { time: moment(member.mutedUntil) })
    : t("chat.isOff");
}

function choice(label: string, pressed: boolean, attrs: string): string {
  return `<button type="button" class="quick-pick" aria-pressed="${pressed ? "true" : "false"}" ${attrs}>${esc(label)}</button>`;
}

/** The fundraisers this person may see in the chat, for the pins and the committee's picker. */
function openFundraisers(session: Session): data.Campaign[] {
  const committee = session.role === "committee";
  return offers(
    committee ? "campaigns" : "residentGive",
    "desktop",
    session.role,
    store.entitlements,
  )
    ? store.campaigns.filter((campaign) => campaign.status === "open")
    : [];
}

function bubble(message: data.ChatMessage, own: boolean, moderating: boolean): string {
  const { name, door } = nameOf(message);
  const committee = message.role === "committee";
  const classes = `chat-bubble${committee ? " from-committee" : ""}${own ? " own" : ""}`;
  const byline =
    `<span class="chat-by">` +
    (committee ? `<span class="chat-tag">${esc(t("chat.committee"))}</span>` : "") +
    `<strong>${esc(own ? t("chat.you") : name)}</strong>` +
    (door && !own ? `<span>${esc(door)}</span>` : "") +
    `<span>${esc(sentWhen(message.sentAt))}</span></span>`;
  const inner = `${byline}<span class="chat-body">${esc(message.body)}</span>`;
  return moderating
    ? `<button type="button" class="${classes}" data-chat-message="${esc(message.id)}" aria-haspopup="dialog" title="${esc(t("chat.messageActionsHint"))}">${inner}</button>`
    : `<div class="${classes}">${inner}</div>`;
}

function pins(session: Session, fundraisers: data.Campaign[], moderating: boolean): string {
  const room = store.chat;
  const pinnedFundraisers = room.settings.pinnedCampaigns
    .map((id) => fundraisers.find((campaign) => campaign.id === id))
    .filter((campaign): campaign is data.Campaign => campaign !== undefined);
  if (pinnedFundraisers.length === 0 && room.pinned.length === 0) return "";
  const give = session.role === "committee" ? "campaigns" : "give";
  return (
    `<div class="chat-pins">` +
    pinnedFundraisers
      .map(
        (campaign) =>
          `<button type="button" class="chat-fund" data-chat-go="${give}"><span class="chat-fund-top"><strong>${esc(
            campaign.title,
          )}</strong><span class="chat-fund-go">${esc(t("nav.give"))}</span></span>${bar(
            campaign.target === 0 ? 0 : campaign.raised / campaign.target,
          )}<span class="chat-fund-sub">${esc(
            t("resident.raisedOf", {
              raised: money(campaign.raised),
              target: money(campaign.target),
            }),
          )}</span></button>`,
      )
      .join("") +
    room.pinned
      .map((message) => {
        const inner = `<span class="chat-pin-mark" aria-hidden="true">✦</span><span class="chat-pin-text"><strong>${esc(
          nameOf(message).name,
        )}:</strong> ${esc(message.body)}</span>`;
        const label = esc(`${t("chat.pinned")}: ${nameOf(message).name}`);
        return moderating
          ? `<button type="button" class="chat-pin" data-chat-message="${esc(message.id)}" aria-haspopup="dialog" aria-label="${label}">${inner}</button>`
          : `<div class="chat-pin" aria-label="${label}">${inner}</div>`;
      })
      .join("") +
    `</div>`
  );
}

function composer(moderating: boolean, wait: number): string {
  return (
    `<div class="chat-compose">` +
    (moderating && store.chat.settings.locked
      ? `<p class="hint chat-gold">${esc(t("chat.lockedCommittee"))}</p>`
      : "") +
    `<p class="hint${wait > 0 ? "" : " hidden"}" id="chatWait" aria-live="polite">${esc(
      t("chat.wait", { count: wait }),
    )}</p>` +
    `<div class="chat-compose-row"><textarea class="field" id="chatDraft" rows="2" maxlength="${String(
      CHAT_MESSAGE_MAX,
    )}" aria-label="${esc(t("chat.placeholder"))}" placeholder="${esc(
      t("chat.placeholder"),
    )}"></textarea><button type="button" class="btn btn-gold" id="chatSend" disabled>${esc(
      t("chat.send"),
    )}</button></div>` +
    `<p class="hint chat-keys">${esc(t("chat.sendHint"))}</p>` +
    `</div>`
  );
}

function sheet(title: string, body: string): string {
  return (
    `<div class="chat-sheet" id="chatSheet" role="dialog" aria-modal="true" aria-labelledby="chatSheetTitle">` +
    `<div class="card chat-sheet-panel"><div class="chat-sheet-head"><h3 id="chatSheetTitle">${esc(
      title,
    )}</h3><button type="button" class="btn btn-ghost btn-sm" data-chat-close>${esc(
      t("chat.close"),
    )}</button></div>${body}</div></div>`
  );
}

function renamePanel(me: data.ChatMember): string {
  return sheet(
    t("chat.rename"),
    `<div class="form-grid"><div><label for="chatName">${esc(t("chat.nameLabel"))}</label>` +
      `<input class="field" id="chatName" maxlength="${String(CHAT_NAME_MAX)}" autocomplete="nickname" value="${esc(
        me.displayName,
      )}" /><p class="hint">${esc(
        me.role === "committee"
          ? t("chat.nameHintCommittee")
          : t("chat.nameHintResident", { unit: me.unitId ?? "" }),
      )}</p></div></div>` +
      `<div class="actions"><button type="button" class="btn btn-gold" id="chatSaveName">${esc(
        t("chat.saveName"),
      )}</button><button type="button" class="btn btn-ghost" data-chat-close>${esc(
        t("chat.cancel"),
      )}</button></div>`,
  );
}

function managePanel(fundraisers: data.Campaign[], now: Date): string {
  const { settings, members } = store.chat;
  // Residents whose chat is off first, because they are who the committee
  // comes here to find; then by door.
  const residents = members
    .filter((member) => member.role === "resident")
    .sort(
      (a, b) =>
        Number(chatIsOff(b, now)) - Number(chatIsOff(a, now)) ||
        (a.unitId ?? "").localeCompare(b.unitId ?? ""),
    );
  const people =
    residents.length === 0
      ? `<p class="hint">${esc(t("chat.nobodyYet"))}</p>`
      : `<div class="chat-people">${residents
          .map((member) => {
            const { name, door } = nameOf(member);
            const status = standing(member, now);
            return `<button type="button" class="chat-person" data-chat-person="${esc(member.uid)}"><span><strong>${esc(
              door ? `${name} · ${door}` : name,
            )}</strong>${status ? `<span class="chat-off">${esc(status)}</span>` : ""}</span><span aria-hidden="true">›</span></button>`;
          })
          .join("")}</div>`;
  return sheet(
    t("chat.manage"),
    `<h4 class="chat-eyebrow">${esc(t("chat.whoPosts"))}</h4><div class="quick-picks">` +
      choice(t("chat.everyone"), !settings.locked, 'data-chat-locked="no"') +
      choice(t("chat.committeeOnly"), settings.locked, 'data-chat-locked="yes"') +
      `</div><p class="hint">${esc(t("chat.whoPostsHint"))}</p>` +
      `<h4 class="chat-eyebrow">${esc(t("chat.cooldown"))}</h4><div class="quick-picks">` +
      CHAT_COOLDOWNS.map((seconds) =>
        choice(
          cooldownLabel(seconds),
          settings.cooldownSeconds === seconds,
          `data-chat-cooldown="${String(seconds)}"`,
        ),
      ).join("") +
      `</div><p class="hint">${esc(t("chat.cooldownHint"))}</p>` +
      `<h4 class="chat-eyebrow">${esc(t("chat.pinFundraisers"))}</h4>` +
      (fundraisers.length === 0
        ? `<p class="hint">${esc(t("chat.noFundraisers"))}</p>`
        : `<div class="quick-picks">${fundraisers
            .map((campaign) =>
              choice(
                campaign.title,
                settings.pinnedCampaigns.includes(campaign.id),
                `data-chat-fund="${esc(campaign.id)}"`,
              ),
            )
            .join("")}</div><p class="hint">${esc(
            t("chat.pinFundraisersHint", { count: CHAT_PINNED_CAMPAIGNS_MAX }),
          )}</p>`) +
      `<h4 class="chat-eyebrow">${esc(t("chat.people"))}</h4><p class="hint">${esc(
        t("chat.peopleHint"),
      )}</p>${people}` +
      `<div class="actions"><button type="button" class="btn btn-gold" data-chat-close>${esc(
        t("chat.done"),
      )}</button></div>`,
  );
}

/**
 * What the committee can do about one message: pin it, delete it, or go to the
 * person who wrote it. Deleting asks first; a message deleted by a mis-click
 * cannot be put back.
 */
function messagePanel(message: data.ChatMessage): string {
  const { name } = nameOf(message);
  const author = store.chat.members.some((member) => member.uid === message.authorUid);
  return sheet(
    t("chat.messageActions"),
    `<p class="chat-quote">${esc(message.body)}</p><div class="actions">` +
      `<button type="button" class="btn btn-ghost" id="chatPin">${esc(
        message.pinned ? t("chat.unpinMessage") : t("chat.pinMessage"),
      )}</button>` +
      `<button type="button" class="btn btn-danger" id="chatDelete">${esc(t("chat.deleteMessage"))}</button>` +
      (message.role === "resident" && author
        ? `<button type="button" class="btn btn-ghost" id="chatAuthor">${esc(
            t("chat.manageName", { name }),
          )}</button>`
        : "") +
      `</div>`,
  );
}

/**
 * One resident, as the committee sees them in the room. Their chat can be
 * turned off and back on and their name put back to the door. There is no way
 * to take them out of the room: they still live here.
 */
function personPanel(member: data.ChatMember, now: Date): string {
  const { name, door } = nameOf(member);
  const status = standing(member, now);
  return sheet(
    door ? `${name} · ${door}` : name,
    `<p class="${status ? "chat-off" : "hint"}">${esc(status ?? t("chat.isOn"))}</p>` +
      (status
        ? `<div class="actions"><button type="button" class="btn btn-gold" id="chatTurnOn">${esc(
            t("chat.turnOn"),
          )}</button></div>`
        : `<h4 class="chat-eyebrow">${esc(t("chat.turnOff"))}</h4><div class="quick-picks">${CHAT_OFF_SPANS.map(
            (span) => choice(offLabel(span), false, `data-chat-off="${String(span ?? "")}"`),
          ).join("")}</div><p class="hint">${esc(t("chat.turnOffHint"))}</p>`) +
      (member.displayName
        ? `<div class="actions"><button type="button" class="btn btn-ghost" id="chatClearName">${esc(
            t("chat.clearName", { unit: member.unitId ?? "" }),
          )}</button></div>`
        : ""),
  );
}

function panelFor(session: Session, fundraisers: data.Campaign[], now: Date): string {
  const room = store.chat;
  const moderating = session.role === "committee";
  if (panel?.kind === "rename" && room.me) return renamePanel(room.me);
  if (panel?.kind === "manage" && moderating) return managePanel(fundraisers, now);
  if (panel?.kind === "message" && moderating) {
    const id = panel.id;
    const message =
      room.messages.find((item) => item.id === id) ?? room.pinned.find((item) => item.id === id);
    if (message) return messagePanel(message);
  }
  if (panel?.kind === "person" && moderating) {
    const uid = panel.uid;
    const member = room.members.find((item) => item.uid === uid);
    if (member) return personPanel(member, now);
  }
  // Whatever the panel was about has gone: a message somebody else deleted.
  panel = null;
  return "";
}

export function chatView(session: Session): View {
  const room = store.chat;
  const title = t("nav.chat");
  if (room.problem) {
    return {
      title,
      sub: "",
      html: `<div class="card empty" role="alert">${esc(t("chat.problem"))}</div>`,
    };
  }
  const moderating = session.role === "committee";
  const uid = session.user.uid;
  const now = new Date();
  const me = room.me;
  const voice = chatVoice(me, room.settings, now);
  const wait = secondsToWait(me, room.settings.cooldownSeconds, now);
  const fundraisers = openFundraisers(session);

  const foot =
    voice === "off"
      ? `<p class="chat-notice" role="status">${esc(
          me?.mutedUntil ? t("chat.offUntil", { time: moment(me.mutedUntil) }) : t("chat.off"),
        )}</p>`
      : voice === "locked"
        ? `<p class="chat-notice" role="status">${esc(t("chat.locked"))}</p>`
        : composer(moderating, wait);

  const pinned = pins(session, fundraisers, moderating);
  const html =
    `<section class="chat${pinned ? " has-pins" : ""}">` +
    `<div class="chat-head">` +
    (me
      ? `<button type="button" class="btn btn-ghost btn-sm" id="chatRename">${esc(
          t("chat.yourName", { name: nameOf(me).name }),
        )}</button>`
      : "<span></span>") +
    (moderating
      ? `<button type="button" class="btn btn-ghost btn-sm" id="chatManage">${esc(t("chat.manage"))}</button>`
      : "") +
    `</div>` +
    pinned +
    `<div class="chat-log" id="chatLog" role="log" tabindex="0" aria-label="${esc(title)}">` +
    (room.messages.length === 0
      ? `<p class="chat-none">${esc(t("chat.none"))}</p>`
      : room.messages
          .map((message) => bubble(message, message.authorUid === uid, moderating))
          .join("")) +
    `</div>` +
    foot +
    `</section>` +
    panelFor(session, fundraisers, now);

  return { title, sub: "", html, wire: (root, go) => wireChat(root, go, session) };
}

/* -------------------------------------------------------------- wiring -- */

function show(next: Panel): void {
  panel = next;
  focusPanel = next !== null;
  refresh();
}

/**
 * A write, with its refusal said in the chat's own words rather than lost.
 * The button is held while it is in flight, so a second click is not a
 * second message.
 */
function attempt(
  button: HTMLButtonElement | null,
  work: () => Promise<void>,
  then?: () => void,
  failure: Key = "chat.actionFailed",
): void {
  const release = button ? hold(button) : () => undefined;
  work()
    .then(
      () => then?.(),
      (cause: unknown) => {
        console.error("chat: write refused", cause);
        toast(t(failure));
      },
    )
    .finally(release);
}

/**
 * Whether the page scrolls the messages rather than a box inside it: on a
 * phone, where a box inside a page is a scroll inside a scroll and the box to
 * type in is better pinned to the bottom of the screen. The stylesheet decides;
 * this reads its answer.
 */
function paged(): boolean {
  const log = document.getElementById("chatLog");
  return log !== null && getComputedStyle(log).overflowY !== "auto";
}

/**
 * On a computer the chat fills the window below the page's heading, so the
 * conversation gets the screen and the box to type in sits at the bottom of
 * it. On a phone the page scrolls instead and the room takes its natural height.
 */
function fit(section: HTMLElement): void {
  if (paged()) {
    section.style.height = "";
    return;
  }
  const top = section.getBoundingClientRect().top + window.scrollY;
  section.style.height = `${String(Math.max(420, Math.round(window.innerHeight - top - 24)))}px`;
}

/** Puts the countdown and the send button right without redrawing the page. */
function sync(): number {
  const room = store.chat;
  const wait = secondsToWait(room.me, room.settings.cooldownSeconds);
  const line = document.getElementById("chatWait");
  if (line) {
    line.textContent = t("chat.wait", { count: wait });
    line.classList.toggle("hidden", wait <= 0);
  }
  const box = document.getElementById("chatDraft") as HTMLTextAreaElement | null;
  const button = document.getElementById("chatSend") as HTMLButtonElement | null;
  if (button) {
    button.disabled = sending || room.me === null || wait > 0 || (box?.value.trim() ?? "") === "";
  }
  return wait;
}

function send(orgId: string): void {
  const me = store.chat.me;
  const box = document.getElementById("chatDraft") as HTMLTextAreaElement | null;
  if (!me || !box || sending || sync() > 0) return;
  const body = box.value;
  if (body.trim() === "") return;
  // Cleared at once rather than when the server answers: the message shows in
  // the log straight away, and a box still holding it reads as not sent.
  sending = true;
  box.value = "";
  following = true;
  sync();
  data
    .sendChat(orgId, me, body)
    .catch((cause: unknown) => {
      console.error("chat: message refused", cause);
      toast(t("chat.sendFailed"));
      const now = document.getElementById("chatDraft") as HTMLTextAreaElement | null;
      if (now && now.value === "") now.value = body;
    })
    .finally(() => {
      sending = false;
      sync();
    });
}

function wireChat(root: HTMLElement, go: (view: string) => void, session: Session): void {
  const { orgId } = session;
  const uid = session.user.uid;
  const room = store.chat;

  const section = root.querySelector<HTMLElement>(".chat");
  if (section) fit(section);
  const log = root.querySelector<HTMLElement>("#chatLog");
  if (log && !paged()) {
    log.scrollTop = following ? log.scrollHeight : scrolled;
    log.addEventListener("scroll", () => {
      scrolled = log.scrollTop;
      following = log.scrollHeight - log.scrollTop - log.clientHeight < 48;
    });
  } else if (log && following) {
    // After the console has put the page back where it was on this redraw,
    // which it does straight after this returns.
    window.setTimeout(() => window.scrollTo({ top: document.documentElement.scrollHeight }), 0);
  }
  if (!pageWired) {
    pageWired = true;
    window.addEventListener("scroll", () => {
      if (document.getElementById("chatLog") && paged()) {
        following =
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 80;
      }
    });
    window.addEventListener("resize", () => {
      const open = document.querySelector<HTMLElement>(".chat");
      if (open) fit(open);
    });
  }

  const box = root.querySelector<HTMLTextAreaElement>("#chatDraft");
  box?.addEventListener("input", () => sync());
  box?.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      send(orgId);
    }
  });
  root.querySelector("#chatSend")?.addEventListener("click", () => send(orgId));

  // The countdown, and a chat turned off for a while coming back on, are the
  // two things here that change with the clock.
  window.clearInterval(tick);
  const offForAWhile = chatIsOff(room.me) && Boolean(room.me?.mutedUntil);
  if (sync() > 0 || offForAWhile) {
    tick = window.setInterval(() => {
      if (!document.getElementById("chatLog")) {
        window.clearInterval(tick);
        return;
      }
      const wait = sync();
      if (offForAWhile && !chatIsOff(store.chat.me)) {
        window.clearInterval(tick);
        refresh();
      } else if (wait === 0 && !offForAWhile) {
        window.clearInterval(tick);
      }
    }, 1000);
  }

  root.querySelector("#chatRename")?.addEventListener("click", () => show({ kind: "rename" }));
  root.querySelector("#chatManage")?.addEventListener("click", () => show({ kind: "manage" }));
  for (const button of root.querySelectorAll<HTMLButtonElement>("[data-chat-message]")) {
    button.addEventListener("click", () =>
      show({ kind: "message", id: button.dataset.chatMessage ?? "" }),
    );
  }
  for (const button of root.querySelectorAll<HTMLButtonElement>("[data-chat-go]")) {
    button.addEventListener("click", () => go(button.dataset.chatGo ?? "home"));
  }

  const sheetNode = root.querySelector<HTMLElement>("#chatSheet");
  if (!sheetNode) return;
  for (const button of sheetNode.querySelectorAll("[data-chat-close]")) {
    button.addEventListener("click", () => show(null));
  }
  sheetNode.addEventListener("click", (event) => {
    if (event.target === sheetNode) show(null);
  });
  if (!keysWired) {
    keysWired = true;
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && panel !== null && document.getElementById("chatSheet")) {
        show(null);
      }
    });
  }
  if (focusPanel) {
    focusPanel = false;
    // The field when there is one, so a name can be typed straight away;
    // otherwise the first choice, and the close button last of all.
    (
      sheetNode.querySelector<HTMLElement>("input") ??
      sheetNode.querySelector<HTMLElement>(".quick-pick, .actions .btn") ??
      sheetNode.querySelector<HTMLElement>("[data-chat-close]")
    )?.focus();
  }

  const close = () => show(null);

  sheetNode.querySelector("#chatSaveName")?.addEventListener("click", (event) => {
    const name = sheetNode.querySelector<HTMLInputElement>("#chatName")?.value ?? "";
    attempt(
      event.currentTarget as HTMLButtonElement,
      () => data.renameInChat(orgId, uid, name),
      close,
    );
  });
  sheetNode.querySelector("#chatName")?.addEventListener("keydown", (event) => {
    if ((event as KeyboardEvent).key === "Enter") {
      sheetNode.querySelector<HTMLButtonElement>("#chatSaveName")?.click();
    }
  });

  // The room's switches stay open after a change: the committee is often
  // setting more than one.
  const saveRoom = (button: HTMLButtonElement, next: data.ChatSettings) =>
    attempt(button, () => data.saveChatRoom(orgId, uid, next));
  for (const button of sheetNode.querySelectorAll<HTMLButtonElement>("[data-chat-locked]")) {
    button.addEventListener("click", () =>
      saveRoom(button, { ...room.settings, locked: button.dataset.chatLocked === "yes" }),
    );
  }
  for (const button of sheetNode.querySelectorAll<HTMLButtonElement>("[data-chat-cooldown]")) {
    button.addEventListener("click", () =>
      saveRoom(button, {
        ...room.settings,
        cooldownSeconds: Number(button.dataset.chatCooldown),
      }),
    );
  }
  for (const button of sheetNode.querySelectorAll<HTMLButtonElement>("[data-chat-fund]")) {
    button.addEventListener("click", () => {
      const id = button.dataset.chatFund ?? "";
      saveRoom(button, {
        ...room.settings,
        pinnedCampaigns: pinCampaign(
          room.settings.pinnedCampaigns,
          id,
          !room.settings.pinnedCampaigns.includes(id),
        ),
      });
    });
  }
  for (const button of sheetNode.querySelectorAll<HTMLButtonElement>("[data-chat-person]")) {
    button.addEventListener("click", () =>
      show({ kind: "person", uid: button.dataset.chatPerson ?? "" }),
    );
  }

  const acting = panel?.kind === "message" ? panel.id : null;
  if (acting !== null) {
    const message =
      room.messages.find((item) => item.id === acting) ??
      room.pinned.find((item) => item.id === acting);
    sheetNode.querySelector("#chatPin")?.addEventListener("click", (event) => {
      attempt(
        event.currentTarget as HTMLButtonElement,
        () => data.pinChat(orgId, acting, !(message?.pinned ?? false)),
        close,
      );
    });
    sheetNode.querySelector("#chatDelete")?.addEventListener("click", (event) => {
      if (!window.confirm(t("chat.deleteConfirm"))) return;
      attempt(
        event.currentTarget as HTMLButtonElement,
        () => data.removeChat(orgId, acting),
        close,
      );
    });
    sheetNode.querySelector("#chatAuthor")?.addEventListener("click", () => {
      if (message) show({ kind: "person", uid: message.authorUid });
    });
  }

  const person = panel?.kind === "person" ? panel.uid : null;
  if (person !== null) {
    for (const button of sheetNode.querySelectorAll<HTMLButtonElement>("[data-chat-off]")) {
      button.addEventListener("click", () => {
        const raw = button.dataset.chatOff ?? "";
        const span = CHAT_OFF_SPANS.find((item) => String(item ?? "") === raw) ?? null;
        attempt(button, () => data.turnChatOff(orgId, person, span), close);
      });
    }
    sheetNode.querySelector("#chatTurnOn")?.addEventListener("click", (event) => {
      attempt(
        event.currentTarget as HTMLButtonElement,
        () => data.turnChatOn(orgId, person),
        close,
      );
    });
    sheetNode.querySelector("#chatClearName")?.addEventListener("click", (event) => {
      attempt(
        event.currentTarget as HTMLButtonElement,
        () => data.clearChatName(orgId, person),
        close,
      );
    });
  }
}
